/**
 * AJAX cart drawer. Intercepts every add-to-cart form and re-renders itself
 * through the Cart AJAX API's `sections` parameter (static section id
 * "cart-drawer"). Native <dialog> provides focus trap, Esc and backdrop.
 * Without JS, forms post to /cart/add and the header icon links to /cart.
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      // last-write-wins: stale responses (out-of-order fetches) are dropped
      this.requestId = 0;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-cart-drawer-close]')) this.close();
        // a click landing on the dialog itself means the backdrop was hit
        if (event.target === this.dialog) this.close();
        const removeButton = event.target.closest('[data-line-key]');
        if (removeButton) {
          this.change(removeButton.dataset.lineKey, 0, removeButton.closest('[data-line-item]'));
        }
      });

      this.addEventListener('change', (event) => {
        if (!event.target.matches('.quantity-input__field')) return;
        const line = event.target.closest('[data-line-item]');
        if (line) this.change(line.dataset.lineItem, Number(event.target.value), line);
      });

      document.querySelectorAll('[data-cart-drawer-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.open();
        });
      });

      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.action || !form.action.includes('/cart/add')) return;
        event.preventDefault();
        this.add(form, event.submitter);
      });
    }

    open() {
      if (!this.dialog.open) {
        this.dialog.showModal();
        // move focus off the close button so it doesn't flash a focus ring on open
        this.dialog.querySelector('.cart-drawer__inner')?.focus();
      }
    }

    close() {
      this.dialog.close();
    }

    async add(form, submitter) {
      const requestId = ++this.requestId;
      submitter?.setAttribute('aria-busy', 'true');
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');

      try {
        const response = await fetch('/cart/add.js', { method: 'POST', body });
        const data = await response.json();
        if (requestId !== this.requestId) return;
        if (!response.ok) {
          this.open();
          this.showError(data.description || data.message);
          return;
        }
        this.render(data.sections['cart-drawer']);
        if (submitter) submitter.dispatchEvent(new CustomEvent('citrus:spill', { bubbles: true }));
        this.open();
      } catch {
        this.open();
        this.showError();
      } finally {
        submitter?.removeAttribute('aria-busy');
      }
    }

    async change(key, quantity, lineElement) {
      const requestId = ++this.requestId;
      lineElement?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'] }),
        });
        const data = await response.json();
        if (requestId !== this.requestId) return;
        if (!response.ok) {
          lineElement?.removeAttribute('aria-busy');
          this.showError(data.description || data.message);
          return;
        }
        this.render(data.sections['cart-drawer']);
      } catch {
        lineElement?.removeAttribute('aria-busy');
        this.showError();
      }
    }

    render(sectionHtml) {
      if (!sectionHtml) return;
      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const next = parsed.querySelector('.cart-drawer__inner');
      const current = this.querySelector('.cart-drawer__inner');
      if (next && current) current.replaceWith(next);
      this.syncBadge();
    }

    syncBadge() {
      const count = Number(this.querySelector('.cart-drawer__inner')?.dataset.itemCount || 0);
      const cartLink = document.querySelector('.header__cart');
      if (!cartLink) return;
      let badge = cartLink.querySelector('.header__cart-count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'header__cart-count';
          badge.setAttribute('aria-hidden', 'true');
          cartLink.appendChild(badge);
        }
        badge.textContent = count;
      } else {
        badge?.remove();
      }
    }

    showError(message) {
      const errorElement = this.querySelector('[data-cart-error]');
      if (!errorElement) return;
      errorElement.textContent = message || this.dataset.errorFallback;
      errorElement.hidden = false;
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}
